import React, { useState, useEffect, useMemo } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { LoaderCircle, ArrowDown, ArrowUp, CalendarDays, BarChart3, AlertTriangle } from 'lucide-react';
import { MenuCostVsActualData } from '../../../utils/chartsUtils';
import { TimeOption } from '../../../utils/chartLabels';
import { currencyFormatter } from '../../../services/helperService';
import { StatCard } from '../../ui/StatCard';
import { iconBgBlue, iconBgPurple, iconBgRed } from '../../../styling/stylingHelper';
import { useChartTheme } from '../../../utils/chartTheme';

interface MenuCostVsActualChartProps {
  data: MenuCostVsActualData[];
  isAllRestaurants: boolean;
  overallTypeFilter: 'all' | 'food' | 'drinks';
  selectedTimeOption: TimeOption;
  loading?: boolean;
  /** Hide the built-in last/avg/worst KPI tiles (the Cost Percent page renders its own KPIs). */
  hideKpis?: boolean;
  /** Render a dotted target cost% reference line (the restaurant's target, blended on the "all" tab). */
  targetLine?: number | null;
  /** Render the chart even in all-restaurants mode (Cost Percent page shows the weighted aggregate). */
  allowAllRestaurants?: boolean;
}

const WEIGHTED_COLOR = '#18B081';
const ACTUAL_COLOR = '#EF6820';
const INEFFICIENCY_COLOR = '#F04438';
const INEFFICIENCY_FILL = '#FEE4E2';
const IN_PROGRESS_FILL = '#F2F4F7';

type ChartPoint = MenuCostVsActualData & {
  band: [number, number] | null;
  actualSolid: number | null;
  actualDashed: number | null;
};

const periodKeySuffix = (opt: TimeOption): 'Day' | 'Week' | 'Month' | 'Quarter' | 'Period' => {
  switch (opt) {
    case 'day': return 'Day';
    case 'week': return 'Week';
    case 'month': return 'Month';
    case 'quarter': return 'Quarter';
    case 'all':
    default: return 'Period';
  }
};

const formatEuroCompact = (amount: number): string => {
  const abs = Math.abs(amount);
  if (abs >= 1000) {
    return `€${(amount / 1000).toFixed(1)}k`;
  }
  return currencyFormatter.format(amount).replace(/\.00$/, '');
};

export const MenuCostVsActualChart: React.FC<MenuCostVsActualChartProps> = ({
  data,
  isAllRestaurants,
  overallTypeFilter,
  selectedTimeOption,
  loading,
  hideKpis = false,
  targetLine = null,
  allowAllRestaurants = false,
}) => {
  const { t } = useTranslation();
  const theme = useChartTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const hasAnyData = data.some(
    d => d.actualCostPercentage !== null || d.weightedCostPercentage !== null
  );

  // Derive solid/dashed split + band per point. The last full point also belongs
  // in the dashed series so the dashed segment visually connects to the solid one.
  const chartData: ChartPoint[] = useMemo(() => {
    const firstPartialIdx = data.findIndex(d => d.isPartial);
    const bridgeIdx = firstPartialIdx > 0 ? firstPartialIdx - 1 : -1;
    return data.map((d, i) => {
      const band: [number, number] | null =
        d.weightedCostPercentage !== null && d.actualCostPercentage !== null
          ? [d.weightedCostPercentage, d.actualCostPercentage]
          : null;
      const inDashed = d.isPartial || i === bridgeIdx;
      return {
        ...d,
        band,
        actualSolid: d.isPartial ? null : d.actualCostPercentage,
        actualDashed: inDashed ? d.actualCostPercentage : null,
      };
    });
  }, [data]);

  const firstPartial = chartData.find(d => d.isPartial);
  const lastPartial = [...chartData].reverse().find(d => d.isPartial);

  // KPI computations
  const kpis = useMemo(() => {
    const fullPoints = data.filter(d => !d.isPartial && d.inefficiency !== null);
    const lastFullIdx = (() => {
      for (let i = data.length - 1; i >= 0; i--) {
        if (!data[i].isPartial && data[i].inefficiency !== null) return i;
      }
      return -1;
    })();
    const lastFull = lastFullIdx >= 0 ? data[lastFullIdx] : null;
    // Previous full = the most recent full BEFORE lastFullIdx
    let prevFull: MenuCostVsActualData | null = null;
    for (let i = lastFullIdx - 1; i >= 0; i--) {
      if (!data[i].isPartial && data[i].inefficiency !== null) {
        prevFull = data[i];
        break;
      }
    }
    const delta = lastFull && prevFull && lastFull.inefficiency !== null && prevFull.inefficiency !== null
      ? lastFull.inefficiency - prevFull.inefficiency
      : null;
    const lastFullEuroLoss = lastFull && lastFull.inefficiency !== null
      ? (lastFull.inefficiency / 100) * lastFull.totalRevenue
      : null;

    const avg = fullPoints.length > 0
      ? fullPoints.reduce((s, d) => s + (d.inefficiency ?? 0), 0) / fullPoints.length
      : null;
    const avgEuroPerPeriod = fullPoints.length > 0
      ? fullPoints.reduce((s, d) => s + ((d.inefficiency ?? 0) / 100) * d.totalRevenue, 0) / fullPoints.length
      : null;

    const worst = fullPoints.reduce<MenuCostVsActualData | null>((best, d) => {
      if (d.inefficiency === null) return best;
      if (!best || (best.inefficiency ?? -Infinity) < d.inefficiency) return d;
      return best;
    }, null);
    const worstEuroLoss = worst && worst.inefficiency !== null
      ? (worst.inefficiency / 100) * worst.totalRevenue
      : null;

    return {
      lastFull,
      prevFull,
      delta,
      lastFullEuroLoss,
      avg,
      avgEuroPerPeriod,
      worst,
      worstEuroLoss,
      visibleCount: data.length,
    };
  }, [data]);

  const suffix = periodKeySuffix(selectedTimeOption);
  const lastFullLabel = t(`charts.menuCostVsActualLastFull${suffix}`, { label: kpis.lastFull?.label ?? '' });
  const averageLabel = t(`charts.menuCostVsActualAverage${suffix}`, { count: kpis.visibleCount });
  const worstLabel = t(`charts.menuCostVsActualWorst${suffix}`, { label: kpis.worst?.label ?? '' });

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    const point = payload[0]?.payload as ChartPoint | undefined;
    if (!point) return null;
    const euroLoss = point.inefficiency !== null
      ? (point.inefficiency / 100) * point.totalRevenue
      : null;
    return (
      <div className="bg-white dark:bg-background-dark border border-[#F5F5F5] dark:border-border-dark rounded-lg p-2 shadow-lg">
        <p className="text-[#101828] dark:text-textPrimary-dark font-semibold text-[12px] leading-[15px] mb-1">
          {label}{point.isPartial ? ` · ${t('charts.menuCostVsActualInProgress', { label: '' }).trim().replace(/^·\s*/, '')}` : ''}
        </p>
        <div className="flex flex-col gap-1">
          {point.weightedCostPercentage !== null && (
            <p className="font-semibold text-[12px] leading-[15px]" style={{ color: WEIGHTED_COLOR }}>
              {t('charts.menuCostVsActualWeighted')}: {point.weightedCostPercentage.toFixed(1)}%
            </p>
          )}
          {point.actualCostPercentage !== null && (
            <p className="font-semibold text-[12px] leading-[15px]" style={{ color: ACTUAL_COLOR }}>
              {t('charts.menuCostVsActualActual')}: {point.actualCostPercentage.toFixed(1)}%
            </p>
          )}
          {point.inefficiency !== null && (
            <p className="font-semibold text-[12px] leading-[15px]" style={{ color: INEFFICIENCY_COLOR }}>
              {t('charts.menuCostVsActualInefficiency')}: {point.inefficiency.toFixed(1)}pp
              {euroLoss !== null && point.totalRevenue > 0 ? ` (${formatEuroCompact(euroLoss)})` : ''}
            </p>
          )}
        </div>
      </div>
    );
  };

  const ChartContent = () => (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={chartData}
        margin={{ top: 5, right: isMobile ? 0 : 5, left: isMobile ? -20 : -20, bottom: 0 }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.gridLine} />
        <XAxis
          dataKey="label"
          type="category"
          axisLine={false}
          tickLine={false}
          tick={{ fill: theme.axisTick, fontSize: isMobile ? 10 : 12 }}
          dy={10}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fill: theme.axisTick, fontSize: isMobile ? 10 : 12 }}
          tickFormatter={(value: number) => `${value}%`}
        />
        <Tooltip content={<CustomTooltip />} />
        {targetLine !== null && targetLine !== undefined && (
          <ReferenceLine
            y={targetLine}
            stroke={theme.axisTick}
            strokeDasharray="5 5"
            strokeWidth={1.5}
            ifOverflow="extendDomain"
            label={{
              value: t('costPercent.targetLineLabel', { value: targetLine.toFixed(1) }),
              position: 'insideTopRight',
              fill: theme.axisTick,
              fontSize: 11,
            }}
          />
        )}
        {firstPartial && lastPartial && (
          <ReferenceArea
            x1={firstPartial.label}
            x2={lastPartial.label}
            fill={IN_PROGRESS_FILL}
            fillOpacity={0.8}
            stroke="none"
            ifOverflow="visible"
          />
        )}
        <Area
          type="monotone"
          dataKey="band"
          fill={INEFFICIENCY_FILL}
          stroke="none"
          isAnimationActive={false}
          activeDot={false}
          legendType="none"
          connectNulls={false}
        />
        <Line
          type="monotone"
          dataKey="weightedCostPercentage"
          stroke={WEIGHTED_COLOR}
          strokeWidth={isMobile ? 1.5 : 2}
          dot={false}
          activeDot={{ r: isMobile ? 4 : 6, fill: WEIGHTED_COLOR, stroke: '#fff', strokeWidth: 2 }}
          connectNulls={false}
        />
        <Line
          type="monotone"
          dataKey="actualSolid"
          stroke={ACTUAL_COLOR}
          strokeWidth={isMobile ? 1.5 : 2}
          dot={false}
          activeDot={{ r: isMobile ? 4 : 6, fill: ACTUAL_COLOR, stroke: '#fff', strokeWidth: 2 }}
          connectNulls={false}
        />
        <Line
          type="monotone"
          dataKey="actualDashed"
          stroke={ACTUAL_COLOR}
          strokeWidth={isMobile ? 1.5 : 2}
          strokeDasharray="4 4"
          dot={false}
          activeDot={{ r: isMobile ? 4 : 6, fill: ACTUAL_COLOR, stroke: '#fff', strokeWidth: 2 }}
          connectNulls={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );

  const Legend = () => (
    <div className="flex justify-center items-center flex-wrap gap-4 sm:gap-6 mt-3 sm:mt-5">
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-[4px]" style={{ backgroundColor: WEIGHTED_COLOR }} />
        <span className="text-[#475467] dark:text-textSecondary-dark text-[13px] leading-5 font-normal">
          {t('charts.menuCostVsActualWeighted')}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-[4px]" style={{ backgroundColor: ACTUAL_COLOR }} />
        <span className="text-[#475467] dark:text-textSecondary-dark text-[13px] leading-5 font-normal">
          {t('charts.menuCostVsActualActual')}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-[4px]" style={{ backgroundColor: INEFFICIENCY_FILL, border: `1px solid ${INEFFICIENCY_COLOR}55` }} />
        <span className="text-[#475467] dark:text-textSecondary-dark text-[13px] leading-5 font-normal">
          {t('charts.menuCostVsActualInefficiencyBand')}
        </span>
      </div>
    </div>
  );

  const KpiCards = () => {
    const hasLastFull = kpis.lastFull !== null && kpis.lastFull.inefficiency !== null;
    const hasAvg = kpis.avg !== null;
    const hasWorst = kpis.worst !== null && kpis.worst.inefficiency !== null;

    const tileClass = 'bg-[#F9FAFB] dark:bg-background-dark';

    const lastDeltaPill =
      hasLastFull && kpis.lastFull && kpis.lastFull.inefficiency !== null && kpis.delta !== null && kpis.prevFull ? (
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] leading-[18px] font-medium ${
            kpis.delta <= 0
              ? 'bg-[#ECFDF3] dark:bg-green-500/10 text-[#027A48] dark:text-textGreen-dark'
              : 'bg-[#FEF3F2] dark:bg-red-500/10 text-[#B42318] dark:text-textRed-dark'
          }`}
        >
          {kpis.delta <= 0 ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
          {Math.abs(kpis.delta).toFixed(1)} {t('charts.menuCostVsActualVsPrev', { label: kpis.prevFull.label })}
        </span>
      ) : undefined;

    const lastEuroLine =
      hasLastFull && kpis.lastFullEuroLoss !== null && kpis.lastFull && kpis.lastFull.totalRevenue > 0 ? (
        <span className="text-[#475467] dark:text-textTertiary-dark text-[12px] leading-[18px]">
          {t('charts.menuCostVsActualLostThatPeriod', { amount: formatEuroCompact(kpis.lastFullEuroLoss) })}
        </span>
      ) : undefined;

    const avgEuroLine =
      hasAvg && kpis.avgEuroPerPeriod !== null && kpis.avgEuroPerPeriod > 0 ? (
        <span className="text-[#475467] dark:text-textTertiary-dark text-[12px] leading-[18px]">
          {t('charts.menuCostVsActualLostPerPeriod', { amount: formatEuroCompact(kpis.avgEuroPerPeriod) })}
        </span>
      ) : undefined;

    const worstEuroLine =
      hasWorst && kpis.worstEuroLoss !== null && kpis.worst && kpis.worst.totalRevenue > 0 ? (
        <span className="text-[#475467] dark:text-textTertiary-dark text-[12px] leading-[18px]">
          {t('charts.menuCostVsActualLostThatPeriod', { amount: formatEuroCompact(kpis.worstEuroLoss) })}
        </span>
      ) : undefined;

    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        {/* Last full period */}
        <StatCard
          icon={<CalendarDays className="w-6 h-6" style={{ color: '#2E90FA' }} />}
          iconBg={iconBgBlue}
          title={hasLastFull ? lastFullLabel : t('charts.menuCostVsActualNoCompletePeriod')}
          value={hasLastFull && kpis.lastFull && kpis.lastFull.inefficiency !== null ? `${kpis.lastFull.inefficiency.toFixed(1)}pp` : ''}
          subtitle={lastDeltaPill}
          bottom={lastEuroLine}
          className={tileClass}
        />

        {/* Average */}
        <StatCard
          icon={<BarChart3 className="w-6 h-6" style={{ color: '#6938EF' }} />}
          iconBg={iconBgPurple}
          title={averageLabel}
          value={hasAvg && kpis.avg !== null ? `${kpis.avg.toFixed(1)}pp` : ''}
          bottom={avgEuroLine}
          className={tileClass}
        />

        {/* Worst */}
        <StatCard
          icon={<AlertTriangle className="w-6 h-6" style={{ color: '#D92D20' }} />}
          iconBg={iconBgRed}
          title={hasWorst ? worstLabel : t('charts.menuCostVsActualNoCompletePeriod')}
          value={hasWorst && kpis.worst && kpis.worst.inefficiency !== null ? `${kpis.worst.inefficiency.toFixed(1)}pp` : ''}
          bottom={worstEuroLine}
          className={tileClass}
        />
      </div>
    );
  };

  const showAllTabNote = overallTypeFilter === 'all' && !isAllRestaurants && hasAnyData;

  return (
    <div className="w-full bg-white dark:bg-card-dark rounded-[12px] p-5 relative shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]">
      <div className="mb-4 sm:mb-6 flex items-center justify-between w-full">
        <div>
          <h1 className="text-[#101828] dark:text-textPrimary-dark text-[20px] leading-[30px] font-bold mb-1">
            {t('charts.menuCostVsActualTitle')}
          </h1>
          <p className="text-[#475467] dark:text-textSecondary-dark text-[14px] leading-[21px] font-normal">
            {t('charts.menuCostVsActualDescription')}
          </p>
        </div>
        <button
          className="border border-[#D0D5DD] dark:border-borderPrimary-dark rounded-[10px] h-10 pl-2 pr-3 hidden md:flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-backgroundDarker-dark transition-all duration-300"
          onClick={() => setIsOpen(true)}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#475467] dark:text-textSecondary-dark">
            <path d="M5.83317 14.1667H8.33317C8.56928 14.1667 8.7672 14.2466 8.92692 14.4063C9.08664 14.5661 9.1665 14.764 9.1665 15.0001C9.1665 15.2362 9.08664 15.4341 8.92692 15.5938C8.7672 15.7536 8.56928 15.8334 8.33317 15.8334H4.99984C4.76373 15.8334 4.56581 15.7536 4.40609 15.5938C4.24637 15.4341 4.1665 15.2362 4.1665 15.0001V11.6667C4.1665 11.4306 4.24637 11.2327 4.40609 11.073C4.56581 10.9133 4.76373 10.8334 4.99984 10.8334C5.23595 10.8334 5.43387 10.9133 5.59359 11.073C5.75331 11.2327 5.83317 11.4306 5.83317 11.6667V14.1667ZM14.1665 5.83341H11.6665C11.4304 5.83341 11.2325 5.75355 11.0728 5.59383C10.913 5.43411 10.8332 5.23619 10.8332 5.00008C10.8332 4.76397 10.913 4.56605 11.0728 4.40633C11.2325 4.24661 11.4304 4.16675 11.6665 4.16675H14.9998C15.2359 4.16675 15.4339 4.24661 15.5936 4.40633C15.7533 4.56605 15.8332 4.76397 15.8332 5.00008V8.33342C15.8332 8.56953 15.7533 8.76744 15.5936 8.92717C15.4339 9.08689 15.2359 9.16675 14.9998 9.16675C14.7637 9.16675 14.5658 9.08689 14.4061 8.92717C14.2464 8.76744 14.1665 8.56953 14.1665 8.33342V5.83341Z" fill="currentColor" />
          </svg>
          <span className="text-[#667085] dark:text-textQuaternary-dark text-[14px] leading-5 font-semibold">Expand</span>
        </button>
      </div>

      {isAllRestaurants && !allowAllRestaurants ? (
        <div className="bg-yellow-50 dark:bg-amber-500/10 border border-yellow-200 dark:border-amber-800 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-darkOrangeColor-dark text-[14px] leading-5">
            {t('charts.menuCostVsActualAllRestaurantsNote')}
          </p>
        </div>
      ) : !hasAnyData ? (
        loading ? (
          <div className="flex items-center justify-center py-12">
            <LoaderCircle className="h-8 w-8 animate-spin text-textPrimary dark:text-textPrimary-dark" />
          </div>
        ) : (
          <p className="text-[#475467] dark:text-textSecondary-dark text-center py-8">{t('charts.noData')}</p>
        )
      ) : (
        <>
          {!hideKpis && <KpiCards />}
          {showAllTabNote && (
            <div className="mb-3 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2">
              <p className="text-blue-800 dark:text-textBlue-dark text-[12px] leading-[18px]">
                {t('charts.menuCostVsActualAllTabNote')}
              </p>
            </div>
          )}
          <div className="h-[300px] w-full">
            <ChartContent />
          </div>
          <Legend />
        </>
      )}

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#16192466] dark:bg-[#16192466]" onClick={() => setIsOpen(false)}>
          <div className="bg-white dark:bg-card-dark rounded-xl w-full max-w-[1100px] p-5 relative" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h1 className="text-[#101828] dark:text-textPrimary-dark text-[20px] leading-[30px] font-bold mb-1">
                  {t('charts.menuCostVsActualTitle')}
                </h1>
                <p className="text-[#475467] dark:text-textSecondary-dark text-[14px] leading-[21px] font-normal">
                  {t('charts.menuCostVsActualDescription')}
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="border border-[#D0D5DD] dark:border-borderPrimary-dark rounded-[10px] h-10 pl-2 pr-3 flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-backgroundDarker-dark transition-all duration-300"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#475467] dark:text-textSecondary-dark">
                  <path d="M7.49984 12.5001H4.99984C4.76373 12.5001 4.56581 12.4202 4.40609 12.2605C4.24637 12.1008 4.1665 11.9029 4.1665 11.6667C4.1665 11.4306 4.24637 11.2327 4.40609 11.073C4.56581 10.9133 4.76373 10.8334 4.99984 10.8334H8.33317C8.56928 10.8334 8.7672 10.9133 8.92692 11.073C9.08664 11.2327 9.1665 11.4306 9.1665 11.6667V15.0001C9.1665 15.2362 9.08664 15.4341 8.92692 15.5938C8.7672 15.7536 8.56928 15.8334 8.33317 15.8334C8.09706 15.8334 7.89914 15.7536 7.73942 15.5938C7.5797 15.4341 7.49984 15.2362 7.49984 15.0001V12.5001ZM12.4998 7.50008H14.9998C15.2359 7.50008 15.4339 7.57994 15.5936 7.73966C15.7533 7.89939 15.8332 8.0973 15.8332 8.33342C15.8332 8.56953 15.7533 8.76744 15.5936 8.92717C15.4339 9.08689 15.2359 9.16675 14.9998 9.16675H11.6665C11.4304 9.16675 11.2325 9.08689 11.0728 8.92717C10.913 8.76744 10.8332 8.56953 10.8332 8.33342V5.00008C10.8332 4.76397 10.913 4.56605 11.0728 4.40633C11.2325 4.24661 11.4304 4.16675 11.6665 4.16675C11.9026 4.16675 12.1005 4.24661 12.2603 4.40633C12.42 4.56605 12.4998 4.76397 12.4998 5.00008V7.50008Z" fill="currentColor" />
                </svg>
                <span className="text-[#667085] dark:text-textQuaternary-dark text-[14px] leading-5 font-semibold">Collapse</span>
              </button>
            </div>
            {!hideKpis && <KpiCards />}
            <div className="w-full h-[450px]">
              <ChartContent />
            </div>
            <Legend />
          </div>
        </div>
      )}
    </div>
  );
};
